import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Terminal } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

export function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { isModern } = useTheme();

  if (loading && isModern) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--am-canvas)' }} data-testid="auth-loading">
        <div className="text-center" style={{ fontFamily: 'var(--am-font-sans)' }}>
          <div className="w-10 h-10 mx-auto mb-4 rounded-full border-[3px] am-spin" style={{ borderColor: 'var(--am-emerald-100)', borderTopColor: 'var(--am-emerald-600)' }} />
          <p className="text-sm" style={{ color: 'var(--am-ink-3)' }}>Signing you in…</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050003] flex items-center justify-center">
        <div className="text-center">
          <Terminal className="w-12 h-12 text-[#39FF14] mx-auto mb-4 animate-pulse" />
          <div className="text-[#39FF14] font-mono text-sm">
            <span className="cursor-blink">█</span> AUTHENTICATING...
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    // Phase 1 (Feb 2026) — Redirect-aware login. Pass the intended path both
    // via React Router state (fast in-app nav) AND via `?next=` query (cold
    // PWA launch where router state doesn't persist). LoginPage reads either.
    // Only include the path — never the origin — so this is same-origin-only
    // by construction; safeRedirect further validates on the login side.
    const target = `${location.pathname}${location.search || ''}`;
    const search = target && target !== '/login'
      ? `?next=${encodeURIComponent(target)}`
      : '';
    return (
      <Navigate
        to={`/login${search}`}
        state={{ from: location }}
        replace
      />
    );
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Phase 5.9.20/21 — preview-mode pass-through.
    // Operators (super_admin / dispatcher) hitting `/customer*` OR `/driver*`
    // with `?preview=1` are allowed to read-only browse without being bounced
    // to their own dashboard. Lets the SA verify product edits or driver UX
    // inside the same PWA context (cookies + role intact).
    const search = new URLSearchParams(location.search);
    const isPreview = search.get('preview') === '1';
    const operatorRoles = ['super_admin', 'dispatcher'];
    const previewTargets = ['customer', 'driver'];
    if (
      isPreview
      && operatorRoles.includes(user.role)
      && allowedRoles.some((r) => previewTargets.includes(r))
    ) {
      return children;
    }
    // Redirect to appropriate dashboard based on role
    const dashboardRoutes = {
      customer: '/customer',
      driver: '/driver',
      dispatcher: '/dispatch'
    };
    return <Navigate to={dashboardRoutes[user.role] || '/'} replace />;
  }

  return children;
}
